import React from 'react';
import { Route, Routes } from 'react-router-dom';
import DashboardHome from "./compenents/DashhboardHome";
import SessionAdminViewing from "../pages/SessionAdminViewing";
import Membership from "../pages/Membership";
import SessionList from "../pages/SessionList";
import PaymentProviderLinks from "../components/PaymentProviderLinks";
import VenueInformation from "../components/VenueInformation";
import Profile from "../pages/Profile";
import ImportUsers from "../pages/ImportUsers";
import BulkSession from "../pages/BulkSession";
import BulkSessionManagement from "../pages/BulkSessionManagement";
import Subscriptions from "../pages/Subscriptions";
import BulkClass from "../pages/BulkClass";
import ClassList from "../pages/ClassList";
import ClassAdminView from "../pages/ClassAdminView";
import ManageSubscription from "../pages/ManageSubscription";
import VenueTermsManagement from "../pages/VenueTermsManagement";
import TailwindSidebar from "../components/TailwindSidebar";
import FloatingTokenBadge from "../components/FloatingTokenBadge";
import Reports from "../pages/Reports";
import UserAttendanceReport from "../pages/UserAttendanceReport";
import Helper from "../helpers";
import config from "../config";
import { ChevronUpIcon } from '@heroicons/react/24/outline';
import {getVenueUserProfile} from "../Services/UserService.ts";
import {getVenueFeatures} from "../Services/VenueService.ts";
import VenueAttendanceReport from "../pages/VenueAttendanceReport";
import UsersAtRiskReport from "../pages/UsersAtRiskReport";
import SignUpHealthReport from "../pages/SignUpHealthReport";
import PlanManagement from "../pages/PlanManagement";
import MyPlans from "../pages/MyPlans";
import TransactionHistory from "../pages/TransactionHistory";
import TokenHistoryReport from "../pages/TokenHistoryReport";
import SubscriptionOffers from "../pages/SubscriptionOffers";
import BuyTokens from "../pages/BuyTokens";
import VenueTokenSettings from "../pages/VenueTokenSettings";
import PTDirectoryEnquiries from "../pages/PTDirectoryEnquiries";
import VenueDirectoryListing from "../pages/VenueDirectoryListing";
import VenueSettings from "../pages/VenueSettings";
import BulkUserManagement from "../pages/BulkUserManagement";

const baseUrl = config.apiBaseUrl;

const Dashboard = () => {
    const [sidebarOpen, setSidebarOpen] = React.useState(window.innerWidth >= 1024);
    const [isAdmin, setIsAdmin] = React.useState(false);
    const [showScrollTop, setShowScrollTop] = React.useState(false);
    const [userProfile, setUserProfile] = React.useState({});
    const [hasPlans, setHasPlans] = React.useState(false);
    const [venueFeatures, setVenueFeatures] = React.useState(null);

    React.useEffect(() => {
        const checkAdmin = async () => {
            const admin = await Helper.userIsAdmin();
            setIsAdmin(admin);
        };
        
        const handleScroll = () => {
            setShowScrollTop(window.pageYOffset > 300);
        };

        const getVenueUserProfileForPage = async () => {
            const venue = await Helper.getUserVenueId();
            var user = await getVenueUserProfile(venue);
            setUserProfile(user);
        }

        const fetchVenueFeatures = async () => {
            try {
                const venue = await Helper.getUserVenueId();
                if (!venue) { setVenueFeatures([]); return; }
                const features = await getVenueFeatures(venue);
                setVenueFeatures(features);
            } catch (e) {
                console.warn('Could not load venue features:', e);
                setVenueFeatures([]);
            }
        };

        const checkUserPlans = async () => {
            try {
                const venue = await Helper.getUserVenueId();
                const userId = await Helper.getCookie('wh.user.id');
                const token = await Helper.getCookie('wh-jwt');
                
                if (!venue || !userId) return;
                
                const response = await fetch(`${baseUrl}/api/venues/${venue}/plans/users/${userId}/plans`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include'
                });
                
                if (response.ok) {
                    const plans = await response.json();
                    setHasPlans(plans && plans.length > 0);
                }
            } catch (error) {
                console.error('Error checking user plans:', error);
                setHasPlans(false);
            }
        };

        const handleResize = () => {
            // Only auto-manage sidebar state on desktop/tablet, not mobile
            if (window.innerWidth >= 768) {
                if (window.innerWidth < 1024) {
                    setSidebarOpen(false);
                } else {
                    setSidebarOpen(true);
                }
            }
            // On mobile, sidebar is always hidden via CSS, so no state management needed
        };
        
        checkAdmin();
        getVenueUserProfileForPage();
        checkUserPlans();
        fetchVenueFeatures();
        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleResize);
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const handleScrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    return (
        <div className="min-h-screen bg-dark-950">
            {/* Hide sidebar on mobile (screens < 768px), show on tablet and desktop */}
            <div className="hidden md:block">
                <TailwindSidebar 
                    isOpen={sidebarOpen} 
                    toggleSidebar={toggleSidebar}
                    isAdmin={isAdmin}
                    userName={userProfile?.name || ''}
                    hasPlans={hasPlans}
                    venueFeatures={venueFeatures}
                />
            </div>
            
            {/* Mobile navigation badge - only show on mobile */}
            <div className="md:hidden">
                <FloatingTokenBadge />
            </div>
            
            <div className={`transition-all duration-300 ${sidebarOpen ? 'md:ml-64' : 'md:ml-16'}`}>
                <div className="p-1">
                    {/* Hide VenueInformation on mobile to save space */}
                    <div className="hidden md:block">
                        <VenueInformation />
                    </div>
                    
                    <Routes>
                        <Route path="/" element={<DashboardHome venueFeatures={venueFeatures}/>} />
                        <Route path="/sessions" element={<SessionList />} />
                        <Route path="/classes" element={<ClassList />} />
                        <Route path="/manage-subscription" element={<ManageSubscription />} />
                        <Route path="/adminSession" element={<SessionAdminViewing />} />
                        <Route path="/adminClasses" element={<ClassAdminView />} />
                        <Route path="/adminTermsAndConditions" element={<VenueTermsManagement />} />
                        <Route path="/bulk-session" element={<BulkSession />} />
                        <Route path="/bulk-session-management" element={<BulkSessionManagement />} />
                        <Route path="/addmanyclasses" element={<BulkClass />} />
                        <Route path="/membership" element={<Membership />} />
                        <Route path="/paymentproviderlinks" element={<PaymentProviderLinks />} />
                        <Route path="/subscriptions" element={<Subscriptions />} />
                        <Route path="/subscription-offers" element={<SubscriptionOffers />} />
                        <Route path="/import-users/:provider" element={<ImportUsers />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/plans" element={<PlanManagement />} />
                        <Route path="/my-plans" element={<MyPlans />} />
                        <Route path="/transaction-history" element={<TransactionHistory />} />
                        <Route path="/reports" element={<Reports />} />
                        <Route path="/reports/user-attendance" element={<UserAttendanceReport />} />
                        <Route path="/reports/venue-attendance" element={<VenueAttendanceReport />} />
                        <Route path="/reports/users-at-risk" element={<UsersAtRiskReport />} />
                        <Route path="/reports/sign-ups" element={<SignUpHealthReport />} />
                        <Route path="/reports/token-history" element={<TokenHistoryReport />} />
                        <Route path="/buy-tokens" element={<BuyTokens />} />
                        <Route path="/token-settings" element={<VenueTokenSettings />} />
                        <Route path="/pt-enquiries" element={<PTDirectoryEnquiries />} />
                        <Route path="/venue-listing" element={<VenueDirectoryListing />} />
                        <Route path="/venue-settings" element={<VenueSettings />} />
                        <Route path="/bulk-users" element={<BulkUserManagement />} />
                    </Routes>
                </div>
                
                {/* Scroll to top button */}
                {showScrollTop && (
                    <button
                        onClick={handleScrollToTop}
                        className="fixed bottom-4 right-4 p-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full shadow-lg transition-all duration-200 transform hover:scale-105 z-50"
                        aria-label="scroll back to top"
                    >
                        <ChevronUpIcon className="h-5 w-5" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
